import { SupervisorRoleDBTypes } from '../constants';
import { type User } from '../../../contexts';
import { APPLICATION_STATUS_TYPE } from '../../../utils';
import { FundingSource } from './NominationForm/FundingModule/types';
import { HKPFS_NOMINEE_FILTER } from '../../ScholarshipManagement/constants/fundingStatus';

export const supervisorToDoFilter = (user: User | null): any => {
  const { sub: netId = '' } = user ?? {};

  const programmeChoiceFilter = {
    or: [
      {
        programmeChoice: {
          rejectedByFirstChoice: false,
          proposedChiefSupervisor: {
            eq: netId,
          },
        },
      },
      {
        programmeChoice: {
          rejectedByFirstChoice: true,
          proposedChiefSupervisorBackup: {
            eq: netId,
          },
        },
      },
    ],
  };

  const statusFilter = {
    in: [
      APPLICATION_STATUS_TYPE.SUBMITTED,
      APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR,
    ],
  };

  return {
    and: [programmeChoiceFilter, { status: statusFilter }],
  };
};

export const nonProposedFilter = (user: User | null): any => {
  // includes the delegating mode
  const delegationModeOn = process.env.REACT_APP_DELEGATION_ON === 'true';
  const { sub: netId = '' } = user ?? {};
  const originUserDept = user?.userInfo.department ?? '';

  const delegateMode = user?.sub !== user?.userInfo?.netId && delegationModeOn;
  const delegatorDept =
    (user?.delegation ?? []).find(delegate => delegate.netId === netId)
      ?.department ?? '';

  const filter = {
    or: [
      {
        and: [
          { status: { eq: APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL } },
          {
            programmeChoice: {
              department: { eq: delegateMode ? delegatorDept : originUserDept },
            },
          },
        ],
      },
      {
        and: [
          { status: { eq: APPLICATION_STATUS_TYPE.SUBMITTED } },
          {
            programmeChoice: {
              department: { eq: delegateMode ? delegatorDept : originUserDept },
              proposedChiefSupervisor: {
                eq: null,
              },
            },
          },
        ],
      },
    ],
  };

  const statusFilter = {
    or: [
      {
        and: [
          { status: { eq: APPLICATION_STATUS_TYPE.SUBMITTED } },
          {
            programmeChoice: {
              acceptNoResearchIndicator: true,
            },
          },
        ],
      },
      {
        and: [{ status: { eq: APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL } }],
      },
    ],
  };

  return netId === '' ? statusFilter : filter;
};

export const coSupervisorToDoFilter = (user: User | null): any => {
  // TODO: Known issue. As returned application will not clear submitted flag, new added co-supervisor after return may also can access TODO list.
  // And if new co-supervisor submit the declaration before supervisor re-submit, it will cause the wrong application status(Under DoRPgS).
  const { sub: netId = '' } = user ?? {};

  return {
    status: {
      in: [APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR],
    },
    nomination: {
      supervisors: {
        elemMatch: {
          netId: { eq: netId },
          type: { eq: SupervisorRoleDBTypes.CO_SUPERVISOR },
          confirmedAsCoSupervisor: null, // here "null" means he/she is not confirmed as co-supervisor, so go to "to-do"
        },
      },
      submission: {
        submitted: true,
      },
    },
  };
};

export const coSupervisorAllFilter = (user: User | null): any => {
  const { sub: netId = '' } = user ?? {};

  return {
    status: {
      nin: [
        APPLICATION_STATUS_TYPE.CREATED,
        APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING,
        APPLICATION_STATUS_TYPE.SUBMITTED,
      ],
    },
    nomination: {
      supervisors: {
        elemMatch: {
          netId: { eq: netId },
          type: { eq: SupervisorRoleDBTypes.CO_SUPERVISOR },
        },
      },
      submission: {
        submitted: true,
      },
    },
  };
};

export const supervisorApplicationListFilter = (user: User | null): any => {
  // includes the delegating mode
  const { sub: netId = '' } = user ?? {};

  const programmeChoiceFilter = {
    or: [
      {
        programmeChoice: {
          rejectedByFirstChoice: false,
          proposedChiefSupervisor: {
            eq: netId,
          },
        },
      },
      {
        programmeChoice: {
          rejectedByFirstChoice: true,
          proposedChiefSupervisorBackup: {
            eq: netId,
          },
        },
      },
    ],
  };

  const statusFilter = {
    status: {
      nin: [
        APPLICATION_STATUS_TYPE.CREATED,
        APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING,
        APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL,
      ],
    },
  };

  return {
    and: [programmeChoiceFilter, statusFilter],
  };
};

export const supervisorPolishCommentsFilter = (netId: string | null): any => {
  return {
    and: {
      nomination: {
        fundings: {
          source: { eq: FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME },
          status: { in: HKPFS_NOMINEE_FILTER },
        },
      },
      status: {
        nin: [
          APPLICATION_STATUS_TYPE.CREATED,
          APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING,
          APPLICATION_STATUS_TYPE.SUBMITTED,
        ],
      },
      or: [
        {
          programmeChoice: {
            rejectedByFirstChoice: false,
            proposedChiefSupervisor: {
              eq: netId,
            },
          },
        },
        {
          programmeChoice: {
            rejectedByFirstChoice: true,
            proposedChiefSupervisorBackup: {
              eq: netId,
            },
          },
        },
      ],
    },
  };
};
